import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';

// Components

import ControlsContainer from './ControlsContainer';
import ButtonContainer from './ButtonContainer';

// ActionCreators / Selectors

import {
    adjustFlow,
    changePage,
    receiveSSNs,
    removeException,
    updateException
} from '../../actions/ssap/ssapActionCreators';
import { setError } from '../../actions/common/actionCreators';
import {
    selectIfInlineEligibilityIgnoredForEscPage,
    selectIsMemberRemovalReasonEnabled,
    selectIsInlineCurrentIncomeEnabled,
    selectIsItfFeatureEnabled,
    selectPrimaryTaxFiler
} from '../../reducers/appReducer';

//Utils

import { familyAnalyzer } from '../../utils/householdMember/familyAnalyzer';
import { filterTools } from '../../utils/tools/filterTools';
import { getCurrentHouseholdMember } from '../../utils/householdMember/getCurrentHouseholdMember';
import { getWomanOfReproductiveAge } from '../../utils/householdMember/getWomanOfReproductiveAge';
import { householdMemberTools } from '../../utils/tools/householdMemberTools';
import { preventerTools } from '../../utils/tools/preventerTools';
import { cloneDeep, isEqual } from '../../utils/miscellaneous/objectHelpers';
import { getDateDifference } from '../../utils/dates/date.utils';

// Constants

import { EXCEPTIONS } from '../../constants/ssap/exceptions/ssapExceptions';
import { INCOME_TYPES } from '../../constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { OTHER_PROGRAMS_TYPES } from '../../constants/ssap/hhmTypes/otherProgramsList';
import { PAGES } from '../../constants/ssap/flows/pagesTypes';
import { SSNS } from '../../APIs/common/endpoints';
import {
    INLINE_ELIGIBILITY_VALUES,
    UNEMPLOYMENT_INCOME_ATTESTATION_SUPPORTED_YEARS
} from '../../constants/ssap/miscellaneous/miscellaneous';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - Page Container
 *
 */
const PagesContainer = props => {

    const { NO, NOT_APPLICABLE, YES } = INLINE_ELIGIBILITY_VALUES;
    const { COBRA_COVERAGE, RETIRE_HEALTH_BENEFITS, VETERANS_AFFAIRS_HCP  } = OTHER_PROGRAMS_TYPES;

    const {
        adjustedFlow = [],
        bloodRelationshipCodes,
        data: {
            coverageYear,
            ssapApplicationId,
            taxHousehold,
            taxHousehold: {
                bloodRelationship = [],
                primaryTaxFilerPersonId
            }
        },
        direction,
        disabledUi,
        environment: {
            localDates: {
                serverDate
            },
            isState
        },
        exception: {
            exceptionType,
            exceptionPages
        },
        loadedRelations,
        flow: {
            isFlowLoaded
        },
        history,
        householdMembers = [],
        householdMembers: {
            length: hhmsQty
        },
        page,
        scroll
    } = props;

    const {
        hhmIndex,
        hhmGuid,
        currentHhm,
    } = getCurrentHouseholdMember(adjustedFlow, page, householdMembers);

    const [
        defaultHomeAddress = {},
        defaultMailingAddress = {}
    ] = taxHousehold.otherAddresses.filter(a => !a.deletable);

    const { addressId: defaultHomeAddressId } = defaultHomeAddress;
    const { addressId: defaultMailingAddressId } = defaultMailingAddress;

    const itfFeatureEnabled = useSelector(selectIsItfFeatureEnabled);
    const inlineEligibilityIgnoredForEscPage = useSelector(selectIfInlineEligibilityIgnoredForEscPage);
    const inlineCurrentIncomeEnabled = useSelector(selectIsInlineCurrentIncomeEnabled);
    const memberRemovalReasonEnabled = useSelector(selectIsMemberRemovalReasonEnabled);
    const primaryTaxFiler = useSelector(selectPrimaryTaxFiler);

    const familyConfig = {
        bloodRelationships: bloodRelationship,
        bloodRelationshipCodes,
        currentMemberIndex: hhmIndex,
        householdMembers,
        isState,
        loadedRelations,
        primaryTaxFilerPersonId,
        serverDate
    };

    const allStatuses = householdMembers.map((hhm, idx) => ({
        isCurrentHhm: hhmGuid === hhm.applicantGuid,
        hhmGuid: hhm.applicantGuid,
        hhmStatus: familyAnalyzer({ ...familyConfig, currentMemberIndex: idx })
    }));

    const familyStatus = hhmIndex !== null ? allStatuses[hhmIndex].hhmStatus : { currentHhm: {} };

    const watchedHhmsProps = householdMembers.map((hhm, idx) => {

        const {
            addressId,
            americanIndianAlaskaNative: {
                americanIndianAlaskaNativeSelfIdentification,
                memberOfFederallyRecognizedTribeIndicator
            },
            applyingForCoverageIndicator,
            citizenshipImmigrationStatus: {
                livedIntheUSSince1996Indicator
            },
            dateOfBirth,
            gender,
            incomes,
            taxHouseholdComposition: {
                married,
                taxFiler,
                taxRelationship
            }
        } = hhm;

        const hhmsProps = [
            addressId,
            americanIndianAlaskaNativeSelfIdentification,
            memberOfFederallyRecognizedTribeIndicator,
            applyingForCoverageIndicator,
            dateOfBirth,
            gender,
            incomes.length,
            livedIntheUSSince1996Indicator,
            married,
            taxFiler,
            taxRelationship
        ];

        return `HOUSEHOLD_MEMBER[${idx}]_PROPS: ${hhmsProps.join(',')}`;
    }).join(' ::: ');

    const watchedProps = Object.entries({
        flow: adjustedFlow.map(p => p.isActive).join(', '),
        flowLength: adjustedFlow.length,
        bloodRelationships: bloodRelationship.map(br => br.relation).join(','),
        exceptionType: exceptionType,
        hhmsQty: hhmsQty,
        primaryTaxFilerPersonId: primaryTaxFilerPersonId,
        watchedHhmsProps: watchedHhmsProps
    }).reduce((res, [ key, value ]) => {
        return [ res, `${key} : ${value}` ].join(' ::: ');
    } , '');

    const [ isPageDataReady, togglePageReadiness ] = React.useState(true);
    const [ isExtraDataRetrieved, toggleExtraDataStatus ] = React.useState(null);

    useEffect(() => { // Exceptions remover
        const conditions = [
            [
                EXCEPTIONS.EDITING_INCOMES_AND_DEDUCTIONS,
                PAGES.PERSONAL_INCOME_SUMMARY_PAGE,
            ],
            [
                EXCEPTIONS.REVIEW_INCOME_SECTION,
                PAGES.INCOME_INFORMATION_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.EDITING_HOUSEHOLD_MEMBER,
                PAGES.HOUSEHOLD_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.EDITING_HOUSEHOLD_MEMBER_ADDRESS,
                PAGES.HOUSEHOLD_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.EDITING_PRIMARY_HOUSEHOLD_CONTACT,
                PAGES.HOUSEHOLD_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.REVIEW_HOUSEHOLD_SECTION,
                PAGES.FAMILY_AND_HOUSEHOLD_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.REVIEW_BEFORE_SUBMIT,
                PAGES.FINAL_APPLICATION_REVIEW_PAGE
            ],
            [
                EXCEPTIONS.REVIEW_BEFORE_SUBMIT_FAMILY_AND_HOUSEHOLD,
                PAGES.FINAL_APPLICATION_REVIEW_PAGE
            ],
            [
                EXCEPTIONS.FIXING_TAX_RELATIONSHIP,
                PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE
            ],
            [
                EXCEPTIONS.EDITING_ADDITIONAL_INFORMATION,
                PAGES.ADDITIONAL_INFORMATION_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.MANAGE_SPOUSE,
                [
                    PAGES.HOUSEHOLD_SUMMARY_PAGE,
                    PAGES.MARITAL_STATUS
                ]
            ]
        ];
        conditions.forEach(([ exceptionTypeToSwitchOff, pageWhereToSwitchOff ]) => {

            const shouldExceptionBeSwitched = (pageWhereToSwitchOff) => {
                if(Array.isArray(pageWhereToSwitchOff)) {
                    return pageWhereToSwitchOff.some(p => p === adjustedFlow[page].pageName);
                } else {
                    return pageWhereToSwitchOff === adjustedFlow[page].pageName;
                }
            };

            if(exceptionTypeToSwitchOff === exceptionType && shouldExceptionBeSwitched(pageWhereToSwitchOff)) {
                props.actions.removeException();
            }
        });
    }, [ exceptionType ]);

    useEffect(() => {
        if(scroll === null) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [ page ]);

    useEffect(() => {

        const isAnyoneFosterCareAgeAndApplyingForCoverage = householdMembers.some(hhm => {
            const age = getDateDifference(serverDate, hhm.dateOfBirth, 'year');
            return age >= 18 && age <= 25 && hhm.applyingForCoverageIndicator;
        });

        const isFullTimeStudent = householdMembers.some(hhm => {
            const age = getDateDifference(serverDate, hhm.dateOfBirth, 'year');
            return age >= 18 && age <= 22 && hhm.applyingForCoverageIndicator;
        });

        const isAnyoneDoesNotResideInTheUSSince1996 = householdMembers.every(hhm => {
            return hhm.citizenshipImmigrationStatus.livedIntheUSSince1996Indicator !== false;
        });

        const prevOutput = cloneDeep(adjustedFlow);

        const output = adjustedFlow.map(el => {

            const hhm = (idx => !~idx ? { name: {} } : householdMembers[el.hhm] ? householdMembers[el.hhm] : { name: {}  })(el.hhm);

            const shouldAskAboutPregnancy = () => {
                return householdMembers.reduce((qty, hhm) => {
                    return getWomanOfReproductiveAge(hhm, serverDate) ? ++qty : qty;
                }, 0);
            };

            const {
                americanIndianAlaskaNative: {
                    americanIndianAlaskaNativeSelfIdentification,
                    memberOfFederallyRecognizedTribeIndicator
                },
                applyingForCoverageIndicator,
                incomes,
                inlineEligibility: {
                    preliminaryAptcCsrStatus,
                    preliminaryChipStatus,
                    preliminaryEmergencyMedicaidStatus,
                    preliminaryMedicaidStatus
                },
                healthCoverage: {
                    currentOtherInsurance: {
                        medicareEligibleIndicator,
                        otherStateOrFederalPrograms,
                        peaceCorpsIndicator,
                        tricareEligibleIndicator
                    }
                },
                personId,
                taxHouseholdComposition: {
                    independentTaxFiler
                }
            } = ~el.hhm && el.hhm <= hhmsQty - 1 ? householdMembers[el.hhm] : {
                americanIndianAlaskaNative: {},
                taxHouseholdComposition: {},
                flagBucket: {},
                inlineEligibility: {},
                healthCoverage: {
                    currentOtherInsurance: {
                        otherStateOrFederalPrograms: []
                    }
                },
                incomes: []
            };

            const checkTribalIncomePage = () => {
                const noEligibleIncomeSources = incomes
                    .filter(filterTools.combinedFilterByType(
                        filterTools.removeDummyType,
                        filterTools.removeSocialSecurityType,
                        filterTools.removeUnemploymentType
                    ))
                    .length === 0;

                const payload = !americanIndianAlaskaNativeSelfIdentification
                    || (americanIndianAlaskaNativeSelfIdentification && noEligibleIncomeSources);

                let explanation;

                switch(true) {
                    case !americanIndianAlaskaNativeSelfIdentification:
                        explanation = 'household member not an AI/AN';
                        break;
                    case noEligibleIncomeSources:
                        explanation = 'household member is AI/AN but no eligible income sources';
                        break;
                    default:
                        explanation = 'something else reason';
                }

                return {
                    payload,
                    explanation
                };
            };

            const checkIfTaxFilersOnApplication = () => {
                return (hhms => hhms.length < 1)(householdMembers
                    .map(hhm => hhm.taxHouseholdComposition.taxFiler ? hhm.personId : null)
                    .filter(el => el));
            };

            const familyStatusForCurrentHhm = (
                s => s
                    ? s.hhmStatus :
                    {
                        currentHhm: {},
                        dependentsOfCaretaker: [],
                        relatedPersonsIds: {}

                    }
            )(allStatuses.find(s => s.hhmGuid === hhm.applicantGuid));

            const checkIfAdditionalInfoPageToBeDisplayed = () => {

                const isMemberTheChildWithoutParent = (()=> {
                    const analyzeIfNotAllParents = value => !Array.isArray(value) ? true : value.length < 2;
                    return applyingForCoverageIndicator && !familyStatusForCurrentHhm.currentHhm.isOlderThanChildAge && analyzeIfNotAllParents(familyStatusForCurrentHhm.relatedPersonsIds.parents);
                })();

                const checkIfIndianHealthServicesShouldBeShown = () => {
                    const inlineEligibilityCriteria = [
                        preliminaryMedicaidStatus,
                        preliminaryEmergencyMedicaidStatus,
                        preliminaryChipStatus
                    ];

                    if (inlineCurrentIncomeEnabled && inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)) {
                        return applyingForCoverageIndicator &&
                            memberOfFederallyRecognizedTribeIndicator &&
                            inlineEligibilityCriteria.some(v => v === YES);
                    } else {
                        return applyingForCoverageIndicator && memberOfFederallyRecognizedTribeIndicator;
                    }
                };

                const checkIfRetroactiveAssistanceShouldBeShown = () => {
                    const inlineEligibilityCriteria = [
                        preliminaryMedicaidStatus,
                        preliminaryEmergencyMedicaidStatus
                    ];

                    if (inlineCurrentIncomeEnabled && inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)) {
                        return applyingForCoverageIndicator && inlineEligibilityCriteria.some(v => v === YES);
                    } else {
                        return applyingForCoverageIndicator;
                    }
                };

                const checkIfAbsentParentShouldBeShown = () => {
                    const inlineEligibilityCriteria = [
                        preliminaryMedicaidStatus,
                        preliminaryEmergencyMedicaidStatus
                    ];

                    if (inlineCurrentIncomeEnabled && inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)) {
                        return isMemberTheChildWithoutParent && inlineEligibilityCriteria.some(v => v === YES);
                    } else {
                        return isMemberTheChildWithoutParent;
                    }
                };

                const checkIfChildrenHaveHealthCoverageShouldBeShown = () => {
                    if (!familyStatusForCurrentHhm.relatedPersonsIds.children || primaryTaxFilerPersonId !== personId) return false;

                    const filteredChildren = familyStatusForCurrentHhm.relatedPersonsIds.children.filter(ch => {
                        const inlineEligibilityCriteria = [
                            preliminaryMedicaidStatus
                        ];

                        if(inlineCurrentIncomeEnabled && inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)) {
                            return !householdMembers[ch - 1].applyingForCoverageIndicator &&
                                preliminaryMedicaidStatus === YES;
                        } else {
                            return !householdMembers[ch - 1].applyingForCoverageIndicator;
                        }
                    });

                    return filteredChildren.length > 0;
                };

                const payload = [
                    checkIfIndianHealthServicesShouldBeShown(),
                    checkIfRetroactiveAssistanceShouldBeShown(),
                    checkIfAbsentParentShouldBeShown(),
                    checkIfChildrenHaveHealthCoverageShouldBeShown()
                ];

                return {
                    payload: !payload.some(condition => condition),
                    explanation: 'Person does not qualify'
                };
            };

            const checkIfShouldSkipIncomeDiscrepancyPage = () => {
                if(!inlineCurrentIncomeEnabled) {
                    return true;
                }

                const isJobIncomeDiscrepancy = householdMembers.some(hhm => {
                    return hhm.incomes.some(
                        income => (income.type === INCOME_TYPES.JOB || income.type === INCOME_TYPES.DUMMY)
                            && !!income.jobIncomeExplanationRequiredIndicator
                    );
                });

                const isTaxHouseholdIncomeDiscrepancy = primaryTaxFiler
                    ? primaryTaxFiler.annualTaxIncome.incomeExplanationRequiredIndicator
                    : false;

                return !isJobIncomeDiscrepancy && !isTaxHouseholdIncomeDiscrepancy;
            };

            const checkEmployerCoverageDetails = () => {

                // Check depending on inlineEligibilityIgnoredForEscPage setting is temporarily added
                // until the decision by Business is made: if we keep or remove updated visibility conditions

                const getIfSkippedBecauseOfBasicEligibility = () => {
                    const age = getDateDifference(serverDate, hhm.dateOfBirth, 'year');

                    if(!hhm.applyingForCoverageIndicator) {
                        return true;
                    }
                    if(age >= 14) {
                        return false;
                    } else {
                        return !allStatuses[el.hhm].hhmStatus.currentHhm.hasParentOrCaretakersWithEsc;
                    }
                };

                if (inlineEligibilityIgnoredForEscPage) {
                    return {
                        payload: getIfSkippedBecauseOfBasicEligibility(),
                        explanation: '- person does not match basic eligibility'
                    };
                }

                const shouldRender = [
                    preliminaryAptcCsrStatus === YES,
                    peaceCorpsIndicator === false,
                    tricareEligibleIndicator === false,
                    medicareEligibleIndicator === false,
                    !otherStateOrFederalPrograms.some(pr => {
                        const { eligible, type } = pr;
                        return eligible &&
                            (type === COBRA_COVERAGE || type === RETIRE_HEALTH_BENEFITS || type === VETERANS_AFFAIRS_HCP);
                    })
                ].every(condition => condition);

                return {
                    payload: (inlineCurrentIncomeEnabled && preliminaryAptcCsrStatus !== NOT_APPLICABLE)
                        ? !(!getIfSkippedBecauseOfBasicEligibility() && shouldRender)
                        : getIfSkippedBecauseOfBasicEligibility(),

                    explanation: inlineCurrentIncomeEnabled
                        ? `person does not match basic eligibility OR
                        "preliminaryAptcCsrStatus" !== YES OR 
                        "peaceCorpsIndicator" !== false OR 
                        "tricareEligibleIndicator" !== false OR 
                        "medicareEligibleIndicator" !== false OR 
                        otherStateOrFederalPrograms contains eligible any of these programs COBRA_COVERAGE / RETIRE_HEALTH_BENEFITS / VETERANS_AFFAIRS_HCP`
                        : '- person does not match basic eligibility'
                };
            };

            const checkStateEmployeeHealthBenefit = () => {
                const shouldNotRender = [
                    preliminaryMedicaidStatus === NO,
                    preliminaryChipStatus === NO,
                    preliminaryEmergencyMedicaidStatus === NO
                ].every(condition => condition);

                return {
                    payload: (
                        inlineCurrentIncomeEnabled &&
                        preliminaryMedicaidStatus !== NOT_APPLICABLE &&
                        preliminaryChipStatus !== NOT_APPLICABLE &&
                        preliminaryEmergencyMedicaidStatus !== NOT_APPLICABLE
                    )
                        ? shouldNotRender
                        : !hhm.applyingForCoverageIndicator,

                    explanation: inlineCurrentIncomeEnabled
                        ? `all three values 
                         "preliminaryMedicaidStatus",
                         "preliminaryChipStatus" and 
                         "preliminaryEmergencyMedicaidStatus" 
                         are equal to NO`
                        : '- person does not apply for coverage'
                };
            };

            /**
             *
             *
             */

            const conditions = [

                // Skipping "Household relationships" and "Household addresses" pages
                // if the only one household member

                [
                    hhmsQty === 1,
                    [
                        PAGES.OTHER_ADDRESSES_PAGE,
                        PAGES.RELATIONSHIPS_PAGE
                    ],
                    '- only one hhm'
                ],

                // Skipping "Military Service" page if all household members with eligible immigrant status reside in US since 1996

                [
                    isAnyoneDoesNotResideInTheUSSince1996,
                    [
                        PAGES.ACTIVE_MILITARY_VETERAN_PAGE
                    ],
                    '- all household members with eligible immigrant status reside in US since 1996'
                ],

                // Skipping "Citizenship/immigration status", "Other Health Coverage" pages
                // if household member do not seek for coverage

                [
                    !hhm.applyingForCoverageIndicator,
                    [
                        PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE,
                        PAGES.OTHER_HEALTH_COVERAGE_PAGE,
                        PAGES.HEALTH_REIMBURSEMENT_ARRANGEMENT_DETAIL_PAGE
                    ],
                    '- not applied for coverage'
                ],

                // Skipping "Employer Coverage Detail" pages due to several conditions (see: checkEmployerCoverageDetails)

                [
                    checkEmployerCoverageDetails().payload,
                    [
                        PAGES.EMPLOYER_COVERAGE_DETAIL_PAGE
                    ],
                    checkEmployerCoverageDetails().explanation
                ],

                // Skipping "Parent/Caretaker" page
                // if household member is not qualified as a caretaker or does not have qualified dependents

                [
                    !familyStatusForCurrentHhm.dependentsOfCaretaker.length,
                    [
                        PAGES.PARENT_CARETAKER_INFORMATION_PAGE
                    ],
                    `- because of caretaker's criteria:
                        -> non-applicant OR 
                        -> age is less than 19 OR
                        -> has no dependents (see below)
                    - because of dependent's criteria:
                        -> age is less than 19 OR
                        -> lives at the same address;
                        -> relationship is not a SPOUSE or FORMER_SPOUSE or DOMESTIC_PARTNER`
                ],

                // Skipping "Tribal income" page
                // if household member does not identify as AI/AN or they do identify but do not have any income

                [
                    checkTribalIncomePage().payload,
                    [
                        PAGES.TRIBAL_SOURCE_INCOME_PAGE
                    ],
                    checkTribalIncomePage().explanation
                ],

                // Skipping "Reconciliation of APTC" page
                // if household member is not primary household contact

                [
                    itfFeatureEnabled
                        ? !(primaryTaxFilerPersonId === personId || independentTaxFiler)
                        : !(primaryTaxFilerPersonId === personId),
                    [
                        PAGES.RECONCILIATION_IN_APTC_PAGE
                    ],
                    itfFeatureEnabled
                        ? '- household member neither Primary Tax Filer nor Independent Tax Filer'
                        : '- applicable only if hhm matches primaryTaxFilerPersonId'
                ],

                // Skipping "Foster care" page
                // if household member's age is not in rage 18 - 25

                [
                    !isAnyoneFosterCareAgeAndApplyingForCoverage,
                    [
                        PAGES.FOSTER_CARE_INFORMATION_PAGE
                    ],
                    '- no one HHM age of 18 - 25 y.o.'
                ],

                // Skipping "FullTime Student" page
                // if applicants's age is not in rage 18 - 22

                [
                    !isFullTimeStudent,
                    [
                        PAGES.FULL_TIME_STUDENT_PAGE
                    ],
                    '- no one HHM age of 18 - 22 y.o.'
                ],

                // Skipping "Pregnancy Information" page
                // if there no any household members who match criteria

                [
                    !shouldAskAboutPregnancy(),
                    [
                        PAGES.PREGNANCY_INFORMATION_PAGE
                    ],
                    '- there are no females age of 9 - 65 y.o.'
                ],

                // Skipping pages for other household members in Income section
                // in review mode started from "Income summary"

                [
                    exceptionType === EXCEPTIONS.REVIEW_INCOME_SECTION
                    && el.hhm > adjustedFlow[exceptionPages[0]].hhm,

                    [
                        PAGES.INCOME_SOURCES_PAGE,
                        PAGES.TRIBAL_SOURCE_INCOME_PAGE,
                        PAGES.DEDUCTION_SOURCES_PAGE,
                        PAGES.EXPECTED_INCOME_PAGE,
                        PAGES.PERSONAL_INCOME_SUMMARY_PAGE
                    ],
                    '- reviewing another household member'
                ],

                // Skipping pages for other household members in Family and Household section
                // in review mode started from "Family and Household summary"

                [
                    exceptionType === EXCEPTIONS.REVIEW_HOUSEHOLD_SECTION
                    && el.hhm > adjustedFlow[exceptionPages[0]].hhm,

                    [
                        PAGES.PERSONAL_INFORMATION_PAGE,
                        PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE,
                        PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE,
                        PAGES.ETHNICITY_AND_RACE_PAGE
                    ],
                    '- reviewing another household member'
                ],

                // Skipping "Primary Tax Filer" page
                // in case if we don't have 1 or more household members (attested as tax filers)

                [
                    checkIfTaxFilersOnApplication(),
                    [
                        PAGES.PRIMARY_TAX_FILER_PAGE
                    ],
                    '- there are no tax filer on SSAP'
                ],

                // Skipping "State Employee Health Benefit" page
                // in different cases

                [
                    checkStateEmployeeHealthBenefit().payload,
                    [
                        PAGES.STATE_EMPLOYEE_HEALTH_BENEFIT_PAGE
                    ],
                    checkStateEmployeeHealthBenefit().explanation
                ],

                // Skipping "Additional Information" page
                // in different cases

                [
                    checkIfAdditionalInfoPageToBeDisplayed().payload, // if this is true...
                    [
                        PAGES.ADDITIONAL_INFORMATION_PAGE             // we skip, and if we skip...
                    ],
                    checkIfAdditionalInfoPageToBeDisplayed().explanation // this is the explanation for why
                ],

                // Skipping "Marital Status" page
                // in case if household member is younger than 14 y.o. OR married

                [
                    !familyStatusForCurrentHhm.currentHhm.isOlderThanMarriageAge ||
                    familyStatusForCurrentHhm.currentHhm.spouseIndex !== null,
                    [
                        PAGES.MARITAL_STATUS
                    ],
                    '- person is married OR less than 14 y.o.'
                ],

                // Skipping "Income Discrepancy" page
                // No one income of job type does not have jobIncomeExplanationRequiredIndicator === true OR
                // PTF's annualTaxIncome does not have incomeExplanationRequiredIndicator === true

                [
                    checkIfShouldSkipIncomeDiscrepancyPage(),
                    [
                        PAGES.INCOME_DISCREPANCY_PAGE
                    ],
                    '- job type does not have jobIncomeExplanationRequiredIndicator === true OR PTF\'s annualTaxIncome does not have incomeExplanationRequiredIndicator === true'
                ]

            ];

            let reasons = [];

            conditions.map(([ value, pages, message ]) => {
                if(value && pages.some(page => page === el.pageName)) {
                    if(Array.isArray(message)) {
                        reasons = [
                            ...reasons,
                            ...message
                        ];
                    } else {
                        reasons = [
                            ...reasons,
                            message
                        ];
                    }
                }
            });

            return {
                ...el,
                isActive: !reasons.length > 0,
                reasons
            };
        });

        const isItEqual = isEqual(prevOutput, output);

        props.actions.adjustFlow(output, isItEqual);
    }, [ watchedProps ]);

    useEffect(() => {
        const {
            page: {
                asyncQueries
            }
        } = preventerTools.provideObject(adjustedFlow[page], { page: { } });

        if(asyncQueries && isExtraDataRetrieved === null) {
            togglePageReadiness(false);
            switch(asyncQueries.queryType) {
                case SSNS:
                    {
                        const guids = householdMemberTools.getApplicantGuids(householdMembers);
                        props.actions.receiveSSNs(
                            { ssapApplicationId },
                            togglePageReadiness,
                            toggleExtraDataStatus,
                            guids
                        );
                    }
                    break;
                default:
                    return null;
            }

        }
    }, [ watchedProps ]);

    if(adjustedFlow.length === 0) {
        return null;
    }

    let CurrentPage;

    let CurrentControls;

    if(isFlowLoaded) {
        try {
            if(adjustedFlow[page].isActive === false && !(direction === 'exact')) {
                let closestEligiblePage = null;
                switch(direction) {
                    case 'increase':
                        closestEligiblePage = adjustedFlow.findIndex((p, idx) => p.isActive && idx > page);
                        break;
                    case 'decrease':
                        closestEligiblePage = adjustedFlow.reduce((closestWithLessIndexPage, p, idx) => {
                            return p.isActive && idx < page ? idx : closestWithLessIndexPage;
                        }, null);
                }
                if(exceptionType && adjustedFlow[exceptionPages[0]].isActive === false ) {
                    props.actions.updateException([ page + 1 ]);
                }
                props.actions.changePage(closestEligiblePage);
                return null;
            }

            if(adjustedFlow[page].hhm !== -1 && currentHhm === undefined) {
                return null;
            }

            const {
                component: Component,
                pageName,
                formName
            } = adjustedFlow[page].page;

            const isUnemploymentIncomeAttestationEnabled =
                UNEMPLOYMENT_INCOME_ATTESTATION_SUPPORTED_YEARS.some(y => y === coverageYear);

            CurrentPage = isPageDataReady ? React.cloneElement(Component,
                {
                    ...(adjustedFlow[page].hhm === -1 ? { allStatuses } : {}),
                    buttonContainer: ButtonContainer,
                    pageName: pageName,
                    familyStatus,
                    formName,
                    history,
                    hhmIndex,
                    currentHhm,
                    isUnemploymentIncomeAttestationEnabled,
                    inlineCurrentIncomeEnabled,
                    itfFeatureEnabled,
                    memberRemovalReasonEnabled,
                    addressDefaults: {
                        defaultHomeAddress,
                        defaultMailingAddress,
                        defaultHomeAddressId,
                        defaultMailingAddressId
                    }
                }) : null;

            CurrentControls = React.cloneElement(<ControlsContainer />,
                {
                    disabledUi,
                    formName,
                    history
                });
        } catch(e) {
            props.actions.setError({
                errorMessage: 'Wrong page number (it does not exist in current flow) has been provided',
                errorType: 'Handling flows'
            });
        }
    }

    return (
        <div className="grid-container padding-x-0 fade-in page-container">
            { CurrentPage }
            { CurrentControls }
        </div>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    bloodRelationshipCodes: state.ssap.relations.codes,
    data: state.ssap.data,
    direction: state.ssap.direction,
    disabledUi: state.ssap.disabledUi,
    exception: state.ssap.exception,
    flow: state.ssap.flow,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    key: `page_key_${state.ssap.page}`, // For fade-in updating then page is changed

    page: state.ssap.page,
    loadedRelations: state.ssap.relations.loadedRelations,
    scroll: state.ssap.scroll
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        adjustFlow,
        changePage,
        receiveSSNs,
        removeException,
        setError,
        updateException
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(PagesContainer);
